import HighlightedPips from '@/components/common/HighlightedPips';
import InstrumentInfo, { findInstrumentInfo } from '@/components/common/InstrumentInfo';
import { tickerIdleTimer } from '@/config/config';
import { isDebugInstance } from '@/helpers/environmentHelper';
import { Modify } from '@/model/common';
import { switchOrderBook } from '@/services/MarketService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import {
    addFavouritePair,
    PairMap,
    removeFavouritePair,
    selectFavouriteMarketPairs
} from '@/state/reducers/marketPairSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { GetColorIndexesReturn, getInstrumentConfig, GetPipIndexesReturn } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useLatencyIdle } from '@/utils/hooks/useLatencyIdle';
import useTicker from '@/utils/hooks/useTicker';
import { useTickerIdle } from '@/utils/hooks/useTickerIdle';
import { useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { CSSProperties, Fragment, useMemo } from 'react';
import { MdStar, MdStarOutline } from 'react-icons/md';

export interface UserMarketProps {
    market: PairMap;
    style: CSSProperties;
    parentSize?: number;
    isActive?: boolean;
    onClick?: () => void;
    onActive?: () => void;
}

const sizeThreshold = 800;

const UserMarket = (props: UserMarketProps) => {
    const { market, style, parentSize = 0, isActive, onClick, onActive } = props;

    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);

    const isXplorSpot = !location.pathname.includes('/trader');

    const { instrumentsIncludeRatioApi } = useSso();
    const { data: instruments } = instrumentsIncludeRatioApi;

    const publicInstrumentConfig = useMemo(() => {
        return getInstrumentConfig(accountType, instrumentConfigs, market?.celer);
    }, [market, instrumentConfigs, accountType]);

    const detailedInstrumentConfig = useMemo(() => {
        return instruments?.find((instrument) => {
            return findInstrumentInfo(isXplorSpot, publicInstrumentConfig, instrument, market);
        });
    }, [publicInstrumentConfig, instruments, market]);

    const generalInfo = useMemo(() => {
        return {
            description: detailedInstrumentConfig?.description || '',
            cfi: detailedInstrumentConfig?.cfi || '',
            upi: detailedInstrumentConfig?.upi || ''
        };
    }, [detailedInstrumentConfig]);

    const tradeInfo = useMemo(() => {
        return {
            type: publicInstrumentConfig.type
        };
    }, [publicInstrumentConfig]);

    return (
        <Fragment>
            {market.celer === 'header' ? (
                <MarketListHeader
                    style={style}
                    parentSize={parentSize}
                    generalInfo={generalInfo}
                    tradeInfo={tradeInfo}
                />
            ) : (
                <div
                    style={style}
                    onClick={() => {
                        switchOrderBook(market, 'spot');
                        onClick && onClick();
                    }}>
                    <div
                        className={cn(
                            'cursor-pointer flex text-xs py-1.5 px-2 text-brand-text justify-between hover:bg-brand-neutral-700',
                            { 'bg-brand-background-dark': isActive }
                        )}
                        onClick={onActive}>
                        <MarketInstrument {...props} />
                        <BidAsks generalInfo={generalInfo} tradeInfo={tradeInfo} {...props} />
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default UserMarket;

type MarketInstrumentProps = Modify<
    Omit<UserMarketProps, 'style'>,
    {
        parentSize?: number;
        isMobileSubHeader?: boolean;
    }
>;

export const MarketInstrument = ({ market, parentSize = 0, isMobileSubHeader }: MarketInstrumentProps) => {
    const [base, quote] = market.show.split('/');
    const instrumentConfig = market.config;
    const futureSymbol = useMemo(() => {
        if (instrumentConfig?.type === 'Future') return instrumentConfig?.gcexSymbol.split('/');
    }, [instrumentConfig]);

    return (
        <div className="min-w-28 flex items-center">
            {/* {parentSize <= sizeThreshold && (
            )} */}
            <InstrumentInfo
                market={market}
                offsetOverride={{
                    mainAxis: 4,
                    crossAxis: 4
                }}
            />
            {parentSize > sizeThreshold && (
                <div
                    title="Instrument Info"
                    className={cn('h-full flex items-center py-2.5 cursor-pointer opacity-0', {})}></div>
            )}
            <div className="flex items-center leading-none">
                <span className="">{futureSymbol ? futureSymbol[0] : base}</span>
                <span className="text-brand-neutral-400">/{futureSymbol ? futureSymbol[1] : quote}</span>
            </div>
        </div>
    );
};

interface MarketListHeaderProps {
    ref?: React.Ref<HTMLDivElement>;
    style: CSSProperties;
    generalInfo: any;
    tradeInfo: any;
    parentSize?: number;
}

export const MarketListHeader = ({ ref, style, generalInfo, tradeInfo, parentSize = 0 }: MarketListHeaderProps) => {
    const lang = useLanguage();
    return (
        <div ref={ref} style={style} className="flex text-brand-text text-sm px-2 justify-between">
            <div className="min-w-28">{lang.blotters.instrument}</div>
            <div className="flex gap-2">
                {parentSize > sizeThreshold && (
                    <Fragment>
                        <div className="min-w-56 whitespace-nowrap">
                            <span>{lang.common.description}</span>
                        </div>
                        <div className="min-w-16 whitespace-nowrap">
                            <span>{lang.blotters.type}</span>
                        </div>
                        {/* <div className="min-w-28 whitespace-nowrap">
                            <span>{lang.blotters.cfi}</span>
                        </div>
                        <div className="min-w-32 whitespace-nowrap">
                            <span>{lang.blotters.upi}</span>
                        </div> */}
                    </Fragment>
                )}
                <div
                    className={cn('min-w-20 flex', {
                        'justify-start': parentSize > sizeThreshold,
                        'justify-end': parentSize <= sizeThreshold
                    })}>
                    {lang.orderBook.bid}
                </div>
                <div
                    className={cn('flex items-center px-1', {
                        'justify-start min-w-20': parentSize > sizeThreshold,
                        'justify-center min-w-12': parentSize <= sizeThreshold
                    })}>
                    {parentSize > sizeThreshold && <div>{lang.orderBook.spread}</div>}
                </div>
                <div className="min-w-20 flex justify-start">{lang.orderBook.ask}</div>
                {isDebugInstance && (
                    <div
                        className={cn('min-w-20 flex-1 flex', {
                            'justify-start': parentSize > sizeThreshold,
                            'justify-end': parentSize <= sizeThreshold
                        })}>
                        {lang.common.latencies}
                    </div>
                )}
                <div className="min-w-4 flex items-center justify-center px-1">
                    {/* <div className="hidden 2xl:block">Spread</div> */}
                </div>
            </div>
        </div>
    );
};

interface BidAsksProps extends UserMarketProps {
    generalInfo: any;
    tradeInfo: any;
}

export const BidAsks = ({ market, generalInfo, tradeInfo, parentSize = 0 }: BidAsksProps) => {
    const { formattedBid, formattedAsk, spread, bidHighlights, askHighlights, latency } = useTicker(market);
    const dispatch = useAppDispatch();
    const userFavouriteMarkets = useAppSelector(selectFavouriteMarketPairs);

    const isFavourite = useMemo(() => {
        return userFavouriteMarkets.map((market) => market.celer).includes(market.celer);
    }, [market, userFavouriteMarkets]);

    return (
        <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
                {parentSize > sizeThreshold && (
                    <Fragment>
                        <div className="min-w-56 whitespace-nowrap">
                            <span>{generalInfo.description || '-'}</span>
                        </div>
                        <div className="min-w-16 whitespace-nowrap">
                            <span>{tradeInfo.type || '-'}</span>
                        </div>
                        {/* <div className="min-w-28 whitespace-nowrap">
                            <span>{generalInfo.cfi || '-'}</span>
                        </div>
                        <div className="min-w-32 whitespace-nowrap">
                            <span>{generalInfo.upi || '-'}</span>
                        </div> */}
                    </Fragment>
                )}
                <div
                    className={cn('min-w-20 flex items-center', {
                        'justify-start': parentSize > sizeThreshold,
                        'justify-end': parentSize <= sizeThreshold
                    })}>
                    {formattedBid && <Highlights ticker={formattedBid} highlights={bidHighlights} />}
                </div>
                <div
                    className={cn('flex items-center px-1 leading-none text-brand-neutral-400', {
                        'justify-start min-w-20': parentSize > sizeThreshold,
                        'justify-center min-w-12': parentSize <= sizeThreshold
                    })}>
                    {spread || '-'}
                </div>
                <div className="min-w-20 flex items-center justify-start">
                    {formattedAsk && <Highlights ticker={formattedAsk} highlights={askHighlights} />}
                </div>
                {isDebugInstance && (
                    <div
                        className={cn('min-w-20 flex-1 flex items-center', {
                            'justify-start': parentSize > sizeThreshold,
                            'justify-end': parentSize <= sizeThreshold
                        })}>
                        <Latencies latency={latency} />
                    </div>
                )}
            </div>
            <div
                className="flex items-center cursor-pointer"
                onClick={(e) => {
                    if (isFavourite) dispatch(removeFavouritePair(market));
                    else dispatch(addFavouritePair(market));

                    e.stopPropagation();
                }}>
                {isFavourite ? (
                    <MdStar className="h-4 w-4 fill-yellow-400 hover:fill-yellow-300" />
                ) : (
                    <MdStarOutline className="h-4 w-4 fill-brand-neutral-100 hover:fill-yellow-300" />
                )}
            </div>
        </div>
    );
};

interface HighlightsProps {
    ticker: GetPipIndexesReturn;
    highlights?: GetColorIndexesReturn;
}

export const Highlights = ({ ticker, highlights }: HighlightsProps) => {
    // check if either bid or ask in this ticker is stale for more than 5 seconds.
    // if stale for more than 5 seconds, ticker is idle. if idle, use default colour
    const tickerIdle = useTickerIdle(tickerIdleTimer, highlights);
    return (
        <HighlightedPips
            price={ticker.price}
            boldIndexes={ticker.pipIndexes}
            colorIndexes={!tickerIdle ? highlights?.colorIndexes : []}
            color={!tickerIdle ? highlights?.color : 'text-brand-text'}
        />
    );
};

interface LatenciesProps {
    latency: {
        timestamp: number;
        value: string;
    };
}

export const Latencies = ({ latency }: LatenciesProps) => {
    const tickerIdle = useLatencyIdle(tickerIdleTimer, latency, false);
    return <div>{tickerIdle ? '' : latency.value}</div>;
};
